import { useEffect, useState } from 'react';
import { RULES, can, RollCallConfirm, RollCallConfirmForm, type Detector, type RollCall, type RollCallDetailDoc } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useMe } from '../lib/me';
import { useToast } from '../components/ui';
import { hm } from '../lib/driverState';
import { fmtTime } from '../lib/format';
import { PhotoGrid } from '../components/Photos';
import { useNames } from '../lib/directory';
import { confirmRollCallDetail, rollCallDetails } from '../lib/fs';
import { useStepUp } from '../components/StepUp';
import { useLive } from '../lib/realtime';

type Details = Map<string, RollCallDetailDoc>;
const alc = (d: RollCallDetailDoc | undefined) => (d ? d.alcohol.toFixed(2) : '—');

const STATUS = { waiting: ['確認待ち', 'warn'], confirmed: ['確認済み', 'ok'], remeasure: ['再測定を依頼', ''], rejected: ['乗務させない', 'ng'], not_done: ['未実施', ''] } as const;

/** 点呼・アルコールチェックの確認（仕様書「点呼・アルコールチェックの確認」）。測定値・健康状態・指示事項は Firestore（配車スタッフには見せない） */
export function RollCallPage() {
  const me = useMe();
  const [rows, setRows] = useState<RollCall[] | null>(null);
  const [detectors, setDetectors] = useState<Detector[]>([]);
  const [details, setDetails] = useState<Details>(new Map());
  const names = useNames();
  const { ensureRecent } = useStepUp();
  const canView = !!me && can(me.role, 'viewRecords');
  const load = () => api.get<{ rollCalls: RollCall[] }>('/v1/office/roll-calls').then(async (r) => { setRows(r.rollCalls); setDetails(await rollCallDetails(r.rollCalls.map((x) => x.id))); });
  // 測定値などを見るには、5 分以内に 6 桁を入れ直す（配車スタッフは見られない）
  const reveal = async () => { if (await ensureRecent('点呼の中身（測定値・健康状態）を見る')) load(); };
  useEffect(() => {
    if (canView) void reveal(); else load();
    api.get<{ detectors: Detector[] }>('/v1/office/detectors').then((r) => setDetectors(r.detectors.filter((d) => d.status === 'active')));
  }, []);
  // ドライバーが点呼を送ったら、すぐに出す
  useLive(['rollcalls'], load);
  const canConfirm = !!me && can(me.role, 'confirmRollCall');
  const waiting = (rows ?? []).filter((r) => r.status === 'waiting');
  const done = (rows ?? []).filter((r) => r.status !== 'waiting');
  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      {!canConfirm && <div className="empty">点呼の確認は運行管理者・補助者だけができます</div>}
      {canView && rows && rows.length > 0 && details.size === 0 && <div className="empty">測定値などは、もう一度 6 桁を入れると見られます <button className="btn sm" onClick={reveal}>見る</button></div>}
      {rows === null ? <p className="muted">読み込み中…</p> : waiting.length === 0 ? <div className="empty">確認待ちの点呼はありません。ドライバーがアプリから送ると、ここに出ます</div>
        : waiting.map((r) => <ConfirmCard key={r.id} rc={r} detail={details.get(r.id)} detectors={detectors} canConfirm={canConfirm} onDone={load} />)}
      <section className="panel">
        <table>
          <thead><tr><th>時刻</th><th>運転者</th><th>車両</th><th>区分</th><th>測定値</th><th>確認者</th><th>指示事項</th><th>状態</th></tr></thead>
          <tbody>{done.length === 0 ? <tr><td colSpan={8} className="muted">この 24 時間の記録はまだありません</td></tr> : done.map((r) => (
            <tr key={r.id}><td className="num">{fmtTime(r.at)}</td><td>{names.driver(r.driverId)}</td><td>{r.vehicle}</td><td>{r.kind === 'before' ? '乗務前' : '乗務後'}</td>
              <td className="num" style={{ color: r.alcoholDetected ? 'var(--alert)' : undefined, fontWeight: r.alcoholDetected ? 800 : undefined }}>{alc(details.get(r.id))}</td>
              <td>{names.staff(r.checkedBy)}<small>{r.checkedAt ? fmtTime(r.checkedAt) : ''}</small></td><td>{details.get(r.id)?.instruction ?? '—'}</td>
              <td><span className={`pill ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</span></td></tr>
          ))}</tbody>
        </table>
      </section>
    </div>
  );
}

function ConfirmCard({ rc, detail, detectors, canConfirm, onDone }: { rc: RollCall; detail?: RollCallDetailDoc; detectors: Detector[]; canConfirm: boolean; onDone: () => void }) {
  const toast = useToast();
  const names = useNames();
  const { ensureRecent } = useStepUp();
  const who = names.driver(rc.driverId);
  const [f, setF] = useState({ healthOk: true, healthNote: '', inspectionOk: rc.kind === 'before', detectorId: detectors[0]?.id ?? '', detectorChecked: false, instruction: '' });
  const [err, setErr] = useState('');
  useEffect(() => { if (!f.detectorId && detectors[0]) setF((x) => ({ ...x, detectorId: detectors[0]!.id })); }, [detectors, f.detectorId]);
  const send = async (decision: 'confirm' | 'remeasure' | 'reject') => {
    setErr('');
    const body = { decision, healthOk: f.healthOk, healthNote: f.healthNote || undefined, inspectionOk: rc.kind === 'before' ? f.inspectionOk : undefined, detectorId: f.detectorId, detectorChecked: f.detectorChecked, instruction: f.instruction || undefined };
    const v = RollCallConfirmForm.safeParse(body);
    if (!v.success) { setErr(v.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    const { healthNote, instruction, ...rest } = v.data;
    if (!(await ensureRecent('点呼の確認'))) return;
    try {
      // 先に点呼の中身（健康状態・日常点検・指示事項）を Firestore に書き、そのあと API で確定する
      await confirmRollCallDetail(rc.id, { healthOk: rest.healthOk, healthNote: healthNote ?? null, inspectionOk: rest.inspectionOk, instruction: instruction ?? null });
      await api.post(`/v1/office/roll-calls/${rc.id}/confirm`, RollCallConfirm.parse(rest));
      toast(decision === 'confirm' ? `${who} の点呼を確認しました` : decision === 'remeasure' ? '再測定を依頼しました' : '乗務させないで記録しました');
      onDone();
    } catch (e) { setErr(e instanceof HttpError ? e.message : '送れませんでした'); }
  };
  return (
    <section className="panel" style={{ padding: 18, display: 'grid', gap: 12, borderTop: rc.alcoholDetected ? '4px solid var(--alert)' : undefined }} aria-label={`${who} の点呼`}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 18 }}>{who}</b><span className="pill">{rc.kind === 'before' ? '乗務前' : '乗務後'}</span>
        <span className="muted">{rc.vehicle}・{fmtTime(rc.at)} 送信</span>
        <span className="sp" style={{ flex: 1 }} />
        <span className="num" style={{ fontSize: 22, fontWeight: 800, color: rc.alcoholDetected ? 'var(--alert)' : undefined }}>{alc(detail)} mg/L</span>
      </div>
      {rc.alcoholDetected && <p className="err" style={{ margin: 0 }}>アルコールが検出されました。乗務させられません</p>}
      <PhotoGrid query={`rollCallId=${rc.id}`} />
      {rc.restMin !== null && rc.restMin < RULES.minRestHours * 60 && <p className="err" style={{ margin: 0 }}>前回の勤務から {hm(rc.restMin)}しか経っていません。{RULES.minRestHours} 時間未満は勤務を開始できません（{fmtTime(new Date(Date.parse(rc.at) + (RULES.minRestHours * 60 - rc.restMin) * 60_000).toISOString())} から開始できます）</p>}
      {rc.restMin !== null && rc.restMin >= RULES.minRestHours * 60 && rc.restMin < RULES.restCautionHours * 60 && <p className="muted" style={{ margin: 0, color: '#7a540a' }}>前回の勤務から {hm(rc.restMin)}です。休息が {RULES.restCautionHours} 時間未満です</p>}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <label><input type="checkbox" style={{ width: 'auto' }} checked={f.healthOk} onChange={(e) => setF({ ...f, healthOk: e.target.checked })} /> 健康状態に問題なし（疾病・疲労・睡眠不足）</label>
        {rc.kind === 'before' && <label><input type="checkbox" style={{ width: 'auto' }} checked={f.inspectionOk} onChange={(e) => setF({ ...f, inspectionOk: e.target.checked })} /> 日常点検 実施済み</label>}
        <label><input type="checkbox" style={{ width: 'auto' }} checked={f.detectorChecked} onChange={(e) => setF({ ...f, detectorChecked: e.target.checked })} /> 検知器の動作確認済み</label>
      </div>
      {!f.healthOk && <div className="field"><label htmlFor={`hn-${rc.id}`}>健康状態の問題と、乗務させる理由</label><input id={`hn-${rc.id}`} value={f.healthNote} onChange={(e) => setF({ ...f, healthNote: e.target.value })} /></div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <div className="field"><label htmlFor={`d-${rc.id}`}>使った検知器</label>
          <select id={`d-${rc.id}`} value={f.detectorId} onChange={(e) => setF({ ...f, detectorId: e.target.value })}>{detectors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
        <div className="field"><label htmlFor={`i-${rc.id}`}>指示事項（任意）</label><input id={`i-${rc.id}`} maxLength={200} value={f.instruction} onChange={(e) => setF({ ...f, instruction: e.target.value })} placeholder="例：雨のため速度注意" /></div>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
      {canConfirm && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn danger" onClick={() => send('reject')}>乗務させない</button>
          <button className="btn" onClick={() => send('remeasure')}>再測定を依頼</button>
          <button className="btn pri" disabled={rc.alcoholDetected} onClick={() => send('confirm')}>確認した</button>
        </div>
      )}
    </section>
  );
}
